import { type ApiResponse, type RequestFailure } from '@owl-nest/api-client'
import * as api from '@owl-nest/api-client/latest'
import { type PromiseAction } from '@owl-nest/redux-wrapper'
import * as Enum from '@owl-nest/legacy-enum'

export const TYPE = Enum.create({
  LOAD_ROOT_REQUEST: 'COMMENTS.LOAD_ROOT_REQUEST',
  LOAD_ROOT_FAILURE: 'COMMENTS.LOAD_ROOT_FAILURE',
  LOAD_ROOT_SUCCESS: 'COMMENTS.LOAD_ROOT_SUCCESS',

  CREATE_ROOT_REQUEST: 'COMMENTS.CREATE_ROOT_REQUEST',
  CREATE_ROOT_FAILURE: 'COMMENTS.CREATE_ROOT_FAILURE',
  CREATE_ROOT_SUCCESS: 'COMMENTS.CREATE_ROOT_SUCCESS',

  LOAD_REPLY_REQUEST: 'COMMENTS.LOAD_REPLY_REQUEST',
  LOAD_REPLY_FAILURE: 'COMMENTS.LOAD_REPLY_FAILURE',
  LOAD_REPLY_SUCCESS: 'COMMENTS.LOAD_REPLY_SUCCESS',

  CREATE_REPLY_REQUEST: 'COMMENTS.CREATE_REPLY_REQUEST',
  CREATE_REPLY_FAILURE: 'COMMENTS.CREATE_REPLY_FAILURE',
  CREATE_REPLY_SUCCESS: 'COMMENTS.CREATE_REPLY_SUCCESS',

  DELETE_REQUEST: 'COMMENTS.DELETE_REQUEST',
  DELETE_FAILURE: 'COMMENTS.DELETE_FAILURE',
  DELETE_SUCCESS: 'COMMENTS.DELETE_SUCCESS',
})

export type Action =
  | LoadRootRequest
  | LoadRootFailure
  | LoadRootSuccess
  | CreateRootRequest
  | CreateRootFailure
  | CreateRootSuccess
  | LoadReplyRequest
  | LoadReplyFailure
  | LoadReplySuccess
  | CreateReplyRequest
  | CreateReplyFailure
  | CreateReplySuccess
  | DeleteRequest
  | DeleteFailure
  | DeleteSuccess

type LoadRootRequest = { type: typeof TYPE.LOAD_ROOT_REQUEST }
type LoadRootFailure = { type: typeof TYPE.LOAD_ROOT_FAILURE; failure: RequestFailure }
type LoadRootSuccess = { type: typeof TYPE.LOAD_ROOT_SUCCESS; success: CommentRootSuccess }
type CommentRootSuccess = api.Pageable<api.CommentList<true>> & { cacheId: string; size: number; forceClean: boolean }

type LoadRootConfig = { pagination?: string; forceClean?: boolean }

export function loadRoot(
  parent: { type: 'project'; project: api.Project } | { type: 'news'; news: api.News<boolean, boolean> },
  { pagination = undefined, forceClean = false }: LoadRootConfig = {},
): PromiseAction<ApiResponse<CommentRootSuccess>> {
  return {
    types: [TYPE.LOAD_ROOT_REQUEST, TYPE.LOAD_ROOT_SUCCESS, TYPE.LOAD_ROOT_FAILURE],
    promise: async () => {
      if (parent.type === 'project') {
        const project = parent.project
        const response = await api.api.get.projectComment({
          urlParams: { projectId: String(project.id) },
          getParams: pagination,
        })
        return response.next((success) => {
          return { ...success.body, cacheId: `project-${project.id}`, size: project.comments_count, forceClean }
        })
      }

      if (parent.type === 'news') {
        const news = parent.news
        const response = await api.api.get.newsComment({
          urlParams: { newsId: String(news.id) },
          getParams: pagination,
        })
        return response.next((success) => {
          return { ...success.body, cacheId: `news-${news.id}`, size: news.comments_count, forceClean }
        })
      }
    },
  }
}

type CreateRootRequest = { type: typeof TYPE.CREATE_ROOT_REQUEST }
type CreateRootFailure = { type: typeof TYPE.CREATE_ROOT_FAILURE; failure: RequestFailure }
type CreateRootSuccess = { type: typeof TYPE.CREATE_ROOT_SUCCESS; success: api.Comment<true> }

export function createRoot(
  parent: { type: 'project'; project: api.Project } | { type: 'news'; news: api.News<boolean, boolean> },
  comment: string,
): PromiseAction<ApiResponse<api.Comment<true>>> {
  return {
    types: [TYPE.CREATE_ROOT_REQUEST, TYPE.CREATE_ROOT_SUCCESS, TYPE.CREATE_ROOT_FAILURE],
    promise: async () => {
      if (parent.type === 'project') {
        const response = await api.api.post.projectComment({
          urlParams: { projectId: String(parent.project.id) },
          body: { comment },
        })

        return response.next((success) => success.body)
      }

      if (parent.type === 'news') {
        const response = await api.api.post.newsComment({
          urlParams: { newsId: String(parent.news.id) },
          body: { comment },
        })

        return response.next((success) => success.body)
      }
    },
  }
}

type LoadReplyRequest = { type: typeof TYPE.LOAD_REPLY_REQUEST }
type LoadReplyFailure = { type: typeof TYPE.LOAD_REPLY_FAILURE; failure: RequestFailure }
type LoadReplySuccess = { type: typeof TYPE.LOAD_REPLY_SUCCESS; success: CommentReplySuccess }

type CommentReplySuccess = api.Pageable<api.CommentList<false>> & { parentId: number; size: number }

export function loadReply(
  comment: api.Comment<boolean>,
  pagination: string,
): PromiseAction<ApiResponse<CommentReplySuccess>> {
  return {
    types: [TYPE.LOAD_REPLY_REQUEST, TYPE.LOAD_REPLY_SUCCESS, TYPE.LOAD_REPLY_FAILURE],
    promise: async () => {
      const response = await api.api.get.commentReplies({
        urlParams: { commentId: String(comment.id) },
        getParams: pagination,
      })

      return response.next((success) => ({ ...success.body, parentId: comment.id, size: comment.replies_count }))
    },
  }
}

type CreateReplyRequest = { type: typeof TYPE.CREATE_REPLY_REQUEST }
type CreateReplyFailure = { type: typeof TYPE.CREATE_REPLY_FAILURE; failure: RequestFailure }
type CreateReplySuccess = { type: typeof TYPE.CREATE_REPLY_SUCCESS; success: api.Comment<false> & { parentId: number } }

export function createReply(
  comment: string,
  parentId: number,
): PromiseAction<ApiResponse<api.Comment<false> & { parentId: number }>> {
  return {
    types: [TYPE.CREATE_REPLY_REQUEST, TYPE.CREATE_REPLY_SUCCESS, TYPE.CREATE_REPLY_FAILURE],
    promise: async () => {
      const response = await api.api.post.commentReplies({
        urlParams: { commentId: String(parentId) },
        body: { comment },
      })

      return response.next((success) => ({ ...success.body, parentId }))
    },
  }
}

type DeleteRequest = { type: typeof TYPE.DELETE_REQUEST }
type DeleteFailure = { type: typeof TYPE.DELETE_FAILURE; failure: RequestFailure }
type DeleteSuccess = { type: typeof TYPE.DELETE_SUCCESS; success: { id: number; parentId: number | 'root' } }

export function remove(
  comment: api.Comment<boolean>,
  parentId: number | 'root',
): PromiseAction<ApiResponse<{ id: number; parentId: number | 'root' }>> {
  return {
    types: [TYPE.DELETE_REQUEST, TYPE.DELETE_SUCCESS, TYPE.DELETE_FAILURE],
    promise: async () => {
      const response = await api.api.delete.comment({
        urlParams: { commentId: String(comment.id) },
      })

      return response.next((_) => ({ id: comment.id, parentId }))
    },
  }
}
